import {
  Box,
  Button,
  Step,
  StepContent,
  StepLabel,
  Stepper,
} from "@mui/material";
import { logger } from "@platform/browser-logger";
import { JSX, useState } from "react";
import { FieldValues, UseFormReturn } from "react-hook-form";
import { Typography } from "@platform/design-system";

import { FieldElement } from "../../FieldElement";

import type { ArrayStepperConfig } from "../types";

export interface ArrayStepperProps<
  TCustomType extends string,
  Values extends FieldValues,
> {
  field: ArrayStepperConfig<TCustomType>;
  methods: UseFormReturn<Values>;
}

const STEP_ID_PREFIX = "ds-array-stepper-step";
const SCROLL_DELAY_MS = 100;
const DEFAULT_TRANSITION_DURATION = 400;

const getStepId = (label: string): string => `${STEP_ID_PREFIX}-${label}`;

const scrollToStep = (label: string | undefined): void => {
  if (!label) {
    return;
  }

  setTimeout(() => {
    const stepElement = document.getElementById(getStepId(label));
    stepElement?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, SCROLL_DELAY_MS);
};

export const ArrayStepper = <
  TCustomType extends string,
  Values extends FieldValues,
>({
  field,
  methods,
}: ArrayStepperProps<TCustomType, Values>): JSX.Element => {
  const [activeStep, setActiveStep] = useState(0);

  if (!field.children || field.children.length === 0) {
    logger.warn(`ArrayStepper field "${field.name}" has no children defined.`);
    return <></>;
  }

  const shouldScrollToStep = field.scrollToStepOnChange ?? false;
  const transitionDuration = shouldScrollToStep
    ? 0
    : (field.transitionDuration ?? DEFAULT_TRANSITION_DURATION);

  const handleNext = async (): Promise<void> => {
    const nextStep = activeStep + 1;
    const canContinue =
      (await field.onNext?.({ field, currentStep: nextStep })) ?? true;

    if (canContinue) {
      setActiveStep(nextStep);
      if (shouldScrollToStep && nextStep < field.children.length) {
        scrollToStep(field.children[nextStep].label);
      }
    }
  };

  const handleBack = (): void => {
    const previousStep = activeStep - 1;
    setActiveStep(previousStep);
    if (shouldScrollToStep && previousStep >= 0) {
      scrollToStep(field.children[previousStep].label);
    }
  };

  const handleStep = (step: number) => (): void => {
    setActiveStep(step);
    if (shouldScrollToStep && step >= 0 && step < field.children.length) {
      scrollToStep(field.children[step].label);
    }
  };

  const isOnLastStep = activeStep === field.children.length - 1;
  const showNextButton = !isOnLastStep || field.showLastNext;
  const showBackButton = field.children.length > 1;

  return (
    <Stepper
      activeStep={activeStep}
      orientation="vertical"
      style={{ paddingRight: 16, paddingLeft: 16 }}
    >
      {field.children.map((step, stepIndex) => (
        <Step key={`${field.name}-step-${step.label}`}>
          <StepLabel
            id={getStepId(step.label)}
            onClick={handleStep(stepIndex)}
            sx={{ cursor: "pointer" }}
            data-testid={`${STEP_ID_PREFIX}-label-${step.label}`}
          >
            <Typography variant="header-small"> {step.label}</Typography>
          </StepLabel>
          <StepContent transitionDuration={transitionDuration}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr",
                gap: 3,
                mb: 3,
              }}
            >
              {step.fields.map((childField) => (
                <FieldElement<TCustomType, Values>
                  key={`${step.label}.${field.name}.${childField.name}`}
                  field={childField}
                  methods={methods}
                  parentPath={`${field.name}.${stepIndex}`}
                />
              ))}
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              {showBackButton && (
                <Button
                  disabled={stepIndex === 0}
                  variant="text"
                  onClick={handleBack}
                  data-testid={`${STEP_ID_PREFIX}-back-${step.label}`}
                >
                  Back
                </Button>
              )}
              {showNextButton && (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  data-testid={`${STEP_ID_PREFIX}-next-${step.label}`}
                >
                  Next
                </Button>
              )}
            </Box>
          </StepContent>
        </Step>
      ))}
    </Stepper>
  );
};
